import type { Selection } from '@tiptap/pm/state';
import { compact } from 'lodash';
import type {
  EditorPlugin as EditorPluginType,
  FormattingToolbarButtons,
  IRicosEditor,
  PluginExports,
  RicosEditorPlugin,
  RicosEditorPlugins,
  RicosServices,
  ToolbarSettings,
} from 'ricos-types';
import { isPresent } from 'ts-is-present';

import { EditorPlugin } from './editorPlugin';
import { mergeConfig } from './mergeConfig';
import { RicosPluginAddButtons } from './plugin-add-buttons';
import type { PluginTextButton } from './plugin-text-button';
import { PluginTextButtons } from './plugin-text-button';

export type PluginServices = Omit<
  RicosServices,
  'plugins' | 'editor' | 'zIndexService' | 'getEditorQuery'
>;

export class PluginCollisionError extends Error {}

export class EditorPlugins implements RicosEditorPlugins {
  private plugins: RicosEditorPlugin[] = [];

  private services: PluginServices & { plugins?: RicosEditorPlugins };

  private toolbarSettings: ToolbarSettings;

  static mergeConfig = mergeConfig;

  constructor(services: PluginServices, toolbarSettings: ToolbarSettings) {
    this.services = services;
    this.toolbarSettings = toolbarSettings;
  }

  register(plugin: EditorPluginType) {
    const candidate = EditorPlugin.of(plugin, { ...this.services, plugins: this });
    const duplicate = this.hasDuplicate(candidate);
    if (duplicate) {
      throw new PluginCollisionError(
        `the plugin ${candidate.getType()} conflicts with ${duplicate.getType()}`
      );
    }

    candidate.register();
    this.plugins.push(candidate);
  }

  unregister(plugin: RicosEditorPlugin) {
    plugin.unregister();
    return this.filter(p => !p.equals(plugin));
  }

  destroy() {
    this.plugins.map(p => p.unregister());
    this.plugins = [];
  }

  filter(predicate: (plugin: RicosEditorPlugin) => boolean) {
    this.plugins = this.plugins.filter(predicate);
    return this.plugins;
  }

  asArray() {
    return this.plugins;
  }

  getConfig(type: string) {
    return this.plugins.filter(plugin => plugin.getType() === type)[0]?.getConfig() || {};
  }

  getTextButtons(): FormattingToolbarButtons {
    const textButtons = this.plugins.flatMap(
      plugin => (plugin.getTextButtons() as PluginTextButton[]) || []
    );
    return PluginTextButtons.of(textButtons, this.services, this.toolbarSettings);
  }

  getAddButtons() {
    //maybe use filter class func
    const addButtons = this.plugins.flatMap(plugin => plugin.getAddButtons() || []);
    return new RicosPluginAddButtons(addButtons, this.services, this.toolbarSettings);
  }

  getFloatingToolbarButtons() {
    return this.plugins.flatMap(plugin => plugin.getFloatingToolbarButton()).filter(isPresent);
  }

  getExports() {
    return this.plugins
      .map(plugin => plugin.getExports())
      .filter(isPresent)
      .reduce((integrations, pluginIntegrations) => {
        return { ...integrations, ...pluginIntegrations };
      }, {} as Partial<PluginExports>);
  }

  getVisibleToolbar(selection: Selection, editor: IRicosEditor) {
    const toolbar = this.plugins
      .flatMap(plugin => plugin.getToolbars())
      .find(toolbar => !!toolbar?.isVisible(selection, editor));
    return toolbar;
  }

  getTiptapExtensions() {
    return compact(this.plugins.flatMap(plugin => plugin.getTiptapExtensions?.() || []));
  }

  isFooterToolbarDisabled(selection) {
    const nodeType = selection?.node.type.name;
    if (nodeType) {
      const selectedPlugin = this.plugins.find(
        plugin => plugin?.getTiptapExtensions()?.[0].name === nodeType
      );
      return !!selectedPlugin?.getTiptapExtensions()?.[0].groups.includes('footer-disabled');
    }
    return false;
  }

  private hasDuplicate(plugin: RicosEditorPlugin) {
    return this.plugins.find(p => p.equals(plugin));
  }
}
